import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Building2,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileSpreadsheet,
  Landmark,
  Lock,
  RefreshCw,
  Send,
  UserMinus,
  Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  useDownloadPayrollBankFile,
  usePayrollBankFilePreviews,
  useReleasePayslips,
} from '../../hooks';
import {
  formatDate,
  formatDateRange,
  getTodayInSingaporeTimezone,
} from '../../utils/dateUtils';
import toast from '../../utils/toast';
import AccordionSection from '../UI/AccordionSection';
import ConfirmModal from '../UI/ConfirmModal';
import FormSelect from '../UI/FormSelect';
import Modal from '../UI/Modal';
import PayslipPreviewModal from './PayslipPreviewModal';

const PAYMENT_CLASSIFICATIONS = [
  {
    id: 'worker',
    label: 'Workers',
    description: 'Worker salary payment batch',
  },
  {
    id: 'staff',
    label: 'Staff',
    description: 'Staff salary payment batch',
  },
];
const STAFF_PAYMENT_CLASSIFICATIONS = new Set([
  'staff',
  'managing_director',
  'executive_director',
  'non_executive_director',
  'intern',
  'advisor',
]);

const parseMoney = (value) => Number.parseFloat(value) || 0;

const positiveFinalPaymentTotal = (rows) => rows.reduce(
  (total, row) => total + Math.max(parseMoney(row.final_payment), 0),
  0,
);

const PayrollPaymentStep = ({
  currentRun,
  payrollRuns,
  employeePayrolls,
  departmentProgress,
  flagSummary,
  flagSummaryLoading,
  flagSummaryError,
  allPayrollsLocked,
  unfinalizedPayrollCount,
  selectedPeriod,
  formatCurrency,
  onBack,
  onExportPayrollRegister,
  payrollRegisterExporting,
}) => {
  const { user } = useAuth();
  const [selectedBank, setSelectedBank] = useState('');
  const [activeFileClassification, setActiveFileClassification] = useState('worker');
  const [showBankFileModal, setShowBankFileModal] = useState(false);
  const [showPayslipPreviewModal, setShowPayslipPreviewModal] = useState(false);
  const [showReleasePayslipsModal, setShowReleasePayslipsModal] = useState(false);
  const [supportingDetailsOpen, setSupportingDetailsOpen] = useState(false);
  const [branchCodesReviewed, setBranchCodesReviewed] = useState(false);

  const {
    previews,
    isLoading: previewsLoading,
    isFetching: previewsFetching,
    error: previewsError,
  } = usePayrollBankFilePreviews(currentRun?.id, selectedBank, !!currentRun);
  const downloadBankFileMutation = useDownloadPayrollBankFile();
  const releasePayslipsMutation = useReleasePayslips();

  const included = useMemo(
    () => employeePayrolls.filter((payroll) => !payroll.is_excluded),
    [employeePayrolls],
  );
  const excluded = useMemo(
    () => employeePayrolls.filter((payroll) => payroll.is_excluded),
    [employeePayrolls],
  );
  const workers = useMemo(
    () => included.filter((payroll) => payroll.employee_classification === 'worker'),
    [included],
  );
  const staff = useMemo(
    () => included.filter(
      (payroll) => STAFF_PAYMENT_CLASSIFICATIONS.has(
        payroll.employee_classification,
      ),
    ),
    [included],
  );
  const unclassified = useMemo(
    () => included.filter(
      (payroll) => (
        payroll.employee_classification !== 'worker'
        && !STAFF_PAYMENT_CLASSIFICATIONS.has(payroll.employee_classification)
      ),
    ),
    [included],
  );

  const workerPreview = previews.worker;
  const staffPreview = previews.staff;
  const providerPreview = workerPreview || staffPreview;
  const availableBanks = providerPreview?.available_banks || [];
  const effectiveBankId = selectedBank || providerPreview?.bank?.id || '';
  const currentProvider = availableBanks.find((bank) => bank.id === effectiveBankId)
    || providerPreview?.bank;

  const fallbackWorkerTotal = positiveFinalPaymentTotal(workers);
  const fallbackStaffTotal = positiveFinalPaymentTotal(staff);
  const workerExpectedTotal = workerPreview
    ? parseMoney(workerPreview.expected_total_amount)
    : fallbackWorkerTotal;
  const staffExpectedTotal = staffPreview
    ? parseMoney(staffPreview.expected_total_amount)
    : fallbackStaffTotal;
  const unclassifiedExpectedTotal = positiveFinalPaymentTotal(unclassified);
  const paymentControlTotalsReady = !!workerPreview && !!staffPreview;
  const employeeTransferTotal = (
    workerExpectedTotal
    + staffExpectedTotal
  );

  const workerHasPayments = (workerPreview?.expected_count || 0) > 0;
  const staffHasPayments = (staffPreview?.expected_count || 0) > 0;
  const workerBankReady = !!workerPreview
    && (workerPreview.blocking_issues?.length || 0) === 0
    && (workerPreview.expected_count === 0 || workerPreview.configured);
  const staffBankReady = !!staffPreview
    && (staffPreview.blocking_issues?.length || 0) === 0
    && (staffPreview.expected_count === 0 || staffPreview.configured);
  const workerFileReady = workerHasPayments && !!workerPreview?.ready;
  const staffFileReady = staffHasPayments && !!staffPreview?.ready;

  const calcComplete = !!departmentProgress?.summary?.is_fully_calculated;
  const flagSummaryReady = !flagSummaryLoading && !flagSummaryError && !!flagSummary;
  const errorCount = flagSummaryReady ? flagSummary.error_count || 0 : null;
  const reviewCount = flagSummaryReady ? flagSummary.review_count || 0 : null;
  const readinessChecks = [
    {
      id: 'calculations',
      label: 'Calculations complete',
      pass: calcComplete,
      detail: calcComplete
        ? `All departments calculated (${included.length} employees)`
        : `${departmentProgress?.summary?.completion_percentage || 0}% complete`,
    },
    {
      id: 'errors',
      label: 'No blocking payroll errors',
      pass: flagSummaryReady && errorCount === 0,
      detail: !flagSummaryReady
        ? 'Payroll exception status could not be confirmed'
        : errorCount === 0
        ? 'None outstanding'
        : `${errorCount} error(s) require resolution`,
    },
    {
      id: 'reviews',
      label: 'Review flags acknowledged',
      pass: flagSummaryReady && reviewCount === 0,
      detail: !flagSummaryReady
        ? 'Payroll review status could not be confirmed'
        : reviewCount === 0
        ? 'All reviewed'
        : `${reviewCount} flag(s) awaiting HR review`,
    },
    {
      id: 'locks',
      label: 'All payment records locked',
      pass: allPayrollsLocked,
      detail: allPayrollsLocked
        ? 'Amounts are protected against recalculation'
        : `${unfinalizedPayrollCount} record(s) remain unlocked`,
    },
    {
      id: 'worker-bank',
      label: 'Worker bank data complete',
      pass: workerBankReady,
      detail: !workerHasPayments && workerPreview
        ? 'No positive worker payments — no file required'
        : workerBankReady
        ? `${workerPreview.expected_count} payment(s) validated`
        : `${workerPreview?.blocking_issues?.length || 0} blocking bank issue(s)`,
    },
    {
      id: 'staff-bank',
      label: 'Staff bank data complete',
      pass: staffBankReady,
      detail: !staffHasPayments && staffPreview
        ? 'No positive staff payments — no file required'
        : staffBankReady
        ? `${staffPreview.expected_count} payment(s) validated`
        : `${staffPreview?.blocking_issues?.length || 0} blocking bank issue(s)`,
    },
  ];
  const allChecksPass = readinessChecks.every((check) => check.pass)
    && unclassified.length === 0;

  const today = getTodayInSingaporeTimezone();
  const paymentDeadline = currentRun?.salary_payment_deadline;
  const runPaid = currentRun?.status === 'paid';
  const runApproved = currentRun?.status === 'approved';
  const bankFilesClosed = runApproved || runPaid;
  const isOverdue = !!paymentDeadline
    && paymentDeadline < today
    && !runPaid;
  const payslipsReleased = ['approved', 'paid'].includes(currentRun?.status);
  const canReleasePayslips = !!(
    user?.is_superuser
    || user?.has_admin_access
    || user?.portal_groups?.includes('HR')
  );
  const canConfigureBankFiles = !!(
    user?.is_superuser
    || user?.has_admin_access
  );

  const previousRun = payrollRuns
    .filter((run) => (
      run.run_type === 'regular'
      && run.id !== currentRun?.id
      && run.status !== 'cancelled'
      && run.period_end_date
      && currentRun?.period_start_date
      && run.period_end_date < currentRun.period_start_date
    ))
    .sort((a, b) => (b.period_end_date || '').localeCompare(a.period_end_date || ''))[0]
    || null;
  const previousNet = previousRun ? parseMoney(previousRun.total_net_pay) : null;
  const currentNet = parseMoney(currentRun?.total_net_pay);
  const netDelta = previousNet === null ? null : currentNet - previousNet;
  const netDeltaPercentage = previousNet
    ? (netDelta / previousNet) * 100
    : null;

  const departmentTotals = useMemo(
    () => Object.values(included.reduce((accumulator, payroll) => {
      const name = payroll.employee_department_name || 'Unassigned';
      if (!accumulator[name]) {
        accumulator[name] = { name, count: 0, net: 0 };
      }
      accumulator[name].count += 1;
      accumulator[name].net += parseMoney(payroll.net_pay);
      return accumulator;
    }, {})).sort((a, b) => b.net - a.net),
    [included],
  );

  const openBankFileModal = (classification) => {
    setActiveFileClassification(classification);
    setBranchCodesReviewed(false);
    setShowBankFileModal(true);
  };

  const closeBankFileModal = () => {
    setBranchCodesReviewed(false);
    setShowBankFileModal(false);
  };

  const activePreview = previews[activeFileClassification];
  const activeClassificationLabel = activeFileClassification === 'worker'
    ? 'Worker'
    : 'Staff';
  const activeBranchCodeSummary = activePreview?.branch_code_summary;
  const branchCodeReviewRequired = !!activeBranchCodeSummary?.review_required;
  const branchCodeReviewComplete = (
    !branchCodeReviewRequired || branchCodesReviewed
  );

  const handleBankFileDownload = async () => {
    const bankId = selectedBank || activePreview?.bank?.id;
    if (
      !currentRun?.id
      || !bankId
      || !activePreview?.ready
      || !branchCodeReviewComplete
    ) return;

    try {
      const response = await downloadBankFileMutation.mutateAsync({
        payrollRunId: currentRun.id,
        classification: activeFileClassification,
        bank: bankId,
        branchCodesReviewed,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const extension = activePreview.bank?.file_extension || 'dat';
      link.href = url;
      link.download = filenameMatch
        ? filenameMatch[1]
        : `PAYROLL_BANK_FILE_${activeFileClassification.toUpperCase()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        `${activePreview.bank?.name || 'Bank'} ${activeClassificationLabel.toLowerCase()} file exported`,
      );
      closeBankFileModal();
    } catch (error) {
      let message = 'Failed to export bank file';
      const responseData = error.response?.data;
      if (responseData instanceof Blob) {
        try {
          const parsed = JSON.parse(await responseData.text());
          if (parsed?.error) message = parsed.error;
        } catch (_) {
          // The response was not a JSON error payload.
        }
      } else if (responseData?.error) {
        message = responseData.error;
      }
      toast.error(message);
    }
  };

  if (!currentRun) {
    return (
      <div className="py-12 text-center">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900">No payroll run loaded</h3>
        <p className="text-sm text-gray-500">Run payroll in Step 3 first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center">
          <Landmark className="mr-2 h-5 w-5 flex-shrink-0 text-red-600" />
          <h3 className="text-lg font-semibold text-gray-900">Review &amp; Payment</h3>
        </div>
        <p className="text-sm text-gray-600">
          Reconcile the two employee payment batches, resolve blockers, and generate
          the bank files for upload. Supporting payroll analysis is available below.
        </p>
      </div>

      <section className="rounded-2xl border bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <Landmark className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm text-gray-600">
                Employee Bank Transfer — {currentRun.period_name || selectedPeriod}
              </p>
              <p className="text-3xl font-bold tabular-nums text-gray-900">
                {paymentControlTotalsReady
                  ? formatCurrency(employeeTransferTotal)
                  : 'Validating…'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Sum of positive final payments, including reimbursements and leave encashment.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-start gap-1.5 sm:items-end">
            {runPaid ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                Paid
              </span>
            ) : isOverdue ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700">
                <AlertTriangle className="h-4 w-4" />
                Payment overdue
              </span>
            ) : runApproved ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                Payment files authorised
              </span>
            ) : allChecksPass ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                Payment files ready
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700">
                <Clock className="h-4 w-4" />
                Action required
              </span>
            )}
            {paymentDeadline && (
              <span className={`text-xs ${isOverdue ? 'font-medium text-red-700' : 'text-gray-500'}`}>
                MOM pay deadline: {formatDate(paymentDeadline)}
              </span>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-gray-100 pt-4">
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Workers</p>
            <p className="text-base font-semibold tabular-nums text-gray-900">
              {workerPreview ? formatCurrency(workerExpectedTotal) : '—'}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {workerPreview ? `${workerPreview.expected_count} payment(s)` : 'Validating…'}
            </p>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Staff</p>
            <p className="text-base font-semibold tabular-nums text-gray-900">
              {staffPreview ? formatCurrency(staffExpectedTotal) : '—'}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {staffPreview ? `${staffPreview.expected_count} payment(s)` : 'Validating…'}
            </p>
          </div>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          {currentRun.period_start_date && currentRun.period_end_date && (
            <>Salary period {formatDateRange(currentRun.period_start_date, currentRun.period_end_date)}</>
          )}
          {(currentRun.processed_by_name || currentRun.created_by_name) && (
            <> · Prepared by {currentRun.processed_by_name || currentRun.created_by_name}</>
          )}
          {currentRun.run_date && <> · Run on {formatDate(currentRun.run_date)}</>}
        </p>

        {unclassified.length > 0 && (
          <div
            role="alert"
            className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <p className="text-xs text-red-800">
              {unclassified.length} included employee(s) have no valid Worker or Staff
              classification, representing {formatCurrency(unclassifiedExpectedTotal)}.
              Bank-file reconciliation is blocked.
            </p>
          </div>
        )}
      </section>

      <section className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <label className="form-label-admin flex items-center gap-2">
              <Landmark className="h-4 w-4 text-gray-400" />
              Company debit bank / upload format
            </label>
            <FormSelect
              options={availableBanks.map((bank) => ({
                value: bank.id,
                label: bank.name,
              }))}
              value={effectiveBankId}
              onChange={setSelectedBank}
              placeholder={previewsLoading ? 'Loading supported banks…' : 'Select bank'}
              theme="admin"
              disabled={previewsLoading || previewsFetching}
              ariaLabel="Company debit bank and upload format"
              required
            />
          </div>
          <div className="text-xs text-gray-500 sm:max-w-sm sm:text-right">
            <p>{currentProvider?.file_format || 'Loading bank format…'}</p>
            {currentProvider?.account_masked && (
              <p>Debit account {currentProvider.account_masked}</p>
            )}
            <p>One selected format is applied to both required payment files.</p>
          </div>
        </div>
        {previewsFetching && !previewsLoading && (
          <div
            role="status"
            aria-live="polite"
            className="mt-3 flex items-center gap-2 text-xs text-gray-500"
          >
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Revalidating both payment batches…
          </div>
        )}
        {previewsError && (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <p className="text-xs text-red-800">
              {previewsError.response?.data?.error || 'Unable to validate payment files.'}
            </p>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {PAYMENT_CLASSIFICATIONS.map((classification) => {
          const preview = previews[classification.id];
          const hasPayments = classification.id === 'worker'
            ? workerHasPayments
            : staffHasPayments;
          const fileReady = classification.id === 'worker'
            ? workerFileReady
            : staffFileReady;
          const expectedTotal = classification.id === 'worker'
            ? workerExpectedTotal
            : staffExpectedTotal;
          const blockingCount = preview?.blocking_issues?.length || 0;

          return (
            <section
              key={classification.id}
              className="rounded-2xl border bg-white p-4 shadow-sm sm:p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                    <Users className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <h4 className="font-semibold text-gray-900">
                      {classification.label} Payment File
                    </h4>
                    <p className="text-xs text-gray-500">{classification.description}</p>
                  </div>
                </div>
                {bankFilesClosed ? (
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600">
                    {runPaid ? 'Run paid' : 'Files authorised'}
                  </span>
                ) : !hasPayments && preview ? (
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600">
                    No payments due
                  </span>
                ) : fileReady ? (
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Ready
                  </span>
                ) : (
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Check required
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Expected payments</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {preview?.expected_count ?? '—'}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Expected total</p>
                  <p className="text-lg font-semibold tabular-nums text-gray-900">
                    {preview ? formatCurrency(expectedTotal) : '—'}
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Payment mode</span>
                  <span className="font-medium text-gray-800">GIRO</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Validated for file</span>
                  <span className="font-medium text-gray-800">
                    {preview?.payable_count || 0} · {formatCurrency(preview?.total_amount || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Value date</span>
                  <span className="font-medium text-gray-800">
                    {preview?.value_date ? formatDate(preview.value_date) : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Blocking bank issues</span>
                  <span className={blockingCount > 0 ? 'font-medium text-red-700' : 'font-medium text-green-700'}>
                    {blockingCount}
                  </span>
                </div>
                {(preview?.non_bank_payment_count || 0) > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">Cheque payments excluded</span>
                    <span className="font-medium text-gray-800">
                      {preview.non_bank_payment_count} · {formatCurrency(preview.non_bank_payment_total)}
                    </span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => openBankFileModal(classification.id)}
                disabled={previewsLoading || !preview || !hasPayments || bankFilesClosed}
                className="btn btn-admin-outline mt-4 w-full"
              >
                <Eye className="mr-2 h-4 w-4" />
                {hasPayments
                  ? `Review ${classification.label} File`
                  : 'No File Required'}
              </button>
            </section>
          );
        })}
      </div>

      <section className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
          <h4 className="text-sm font-medium text-gray-900">Payment Readiness</h4>
          {allChecksPass ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
              <CheckCircle className="h-3.5 w-3.5" />
              All checks passed
            </span>
          ) : (
            <button
              type="button"
              onClick={onBack}
              className="text-xs font-medium text-amber-700 underline hover:text-amber-900"
            >
              Resolve payroll issues in Step 3
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-100">
          {readinessChecks.map((check) => (
            <div
              key={check.id}
              className="flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center">
                {check.pass ? (
                  <CheckCircle className="mr-2 h-4 w-4 flex-shrink-0 text-green-500" />
                ) : (
                  <AlertTriangle className="mr-2 h-4 w-4 flex-shrink-0 text-amber-500" />
                )}
                <span className="text-gray-700">{check.label}</span>
              </div>
              <span className={`ml-6 text-xs sm:ml-0 ${check.pass ? 'text-gray-500' : 'font-medium text-amber-700'}`}>
                {check.detail}
              </span>
            </div>
          ))}
          <div className="px-4 py-2.5 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 items-center">
                <UserMinus className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="text-gray-700">Excluded from this run</span>
              </div>
              <span className="text-xs text-gray-500">
                {excluded.length === 0 ? 'None' : `${excluded.length} employee(s)`}
              </span>
            </div>
            {excluded.length > 0 && (
              <ul className="ml-6 mt-2 space-y-1">
                {excluded.slice(0, 5).map((payroll) => (
                  <li key={payroll.id} className="text-xs text-gray-600">
                    <span className="font-medium">
                      {payroll.employee_name || payroll.employee_staff_id}
                    </span>
                    {payroll.exclusion_reason && (
                      <span className="text-gray-500"> — {payroll.exclusion_reason}</span>
                    )}
                  </li>
                ))}
                {excluded.length > 5 && (
                  <li className="text-xs text-gray-400">
                    +{excluded.length - 5} more — see Step 3
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Employee Communications</h4>
            <p className="mt-1 text-xs text-gray-500">
              Release worker payslips only after the payment files have been authorised
              in the bank portal.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowPayslipPreviewModal(true)}
              disabled={workers.length === 0}
              className="btn btn-admin-outline"
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview Payslip
            </button>
            {payslipsReleased ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                Worker Payslips Released
              </span>
            ) : canReleasePayslips ? (
              <button
                type="button"
                onClick={() => setShowReleasePayslipsModal(true)}
                disabled={!allChecksPass || releasePayslipsMutation.isPending}
                className="btn btn-admin-primary"
                title={!allChecksPass
                  ? 'Complete every payment-readiness check first'
                  : 'Make worker payslips visible and notify workers'}
              >
                <Send className="mr-2 h-4 w-4" />
                {releasePayslipsMutation.isPending ? 'Releasing…' : 'Release Worker Payslips'}
              </button>
            ) : (
              <span className="text-xs text-gray-500">
                HR or Admin releases worker payslips.
              </span>
            )}
          </div>
        </div>
      </section>

      <AccordionSection
        expanded={supportingDetailsOpen}
        onToggle={() => setSupportingDetailsOpen((open) => !open)}
        theme="admin"
        contentVariant="padded"
        icon={<BarChart3 className="h-4 w-4 text-gray-600" />}
        iconBg="bg-gray-100"
        title="Supporting Payroll Analysis"
        trailing={(
          <span className="text-xs text-gray-500">
            {included.length} employees · Net {formatCurrency(currentNet)}
          </span>
        )}
      >
        <div className="space-y-5 pt-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Gross Pay</p>
              <p className="font-semibold tabular-nums text-gray-900">
                {formatCurrency(currentRun.total_gross_pay)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Deductions</p>
              <p className="font-semibold tabular-nums text-gray-900">
                {formatCurrency(currentRun.total_deductions)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Employer CPF + SDL</p>
              <p className="font-semibold tabular-nums text-gray-900">
                {formatCurrency(
                  parseMoney(currentRun.total_cpf_employer)
                  + parseMoney(currentRun.total_sdl),
                )}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">FWL Estimate</p>
              <p className="font-semibold tabular-nums text-gray-900">
                {formatCurrency(currentRun.total_foreign_worker_levy)}
              </p>
            </div>
          </div>

          {previousRun && (
            <div className="rounded-xl border border-gray-200 p-3 text-xs text-gray-600">
              Net pay versus {previousRun.period_name}:{' '}
              <span className={`font-semibold ${netDelta >= 0 ? 'text-amber-700' : 'text-green-700'}`}>
                {netDelta >= 0 ? '+' : '−'}{formatCurrency(Math.abs(netDelta))}
                {netDeltaPercentage !== null
                  && ` (${netDeltaPercentage >= 0 ? '+' : '−'}${Math.abs(netDeltaPercentage).toFixed(1)}%)`}
              </span>
            </div>
          )}

          {departmentTotals.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h5 className="text-sm font-medium text-gray-900">Net Pay by Department</h5>
                <span className="text-xs text-gray-500">{departmentTotals.length} departments</span>
              </div>
              <div className="divide-y divide-gray-100">
                {departmentTotals.map((department) => (
                  <div
                    key={department.name}
                    className="flex items-center justify-between px-4 py-2.5 text-sm"
                  >
                    <div className="flex min-w-0 items-center">
                      <Building2 className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="truncate text-gray-700">{department.name}</span>
                      <span className="ml-2 flex-shrink-0 text-xs text-gray-400">
                        {department.count} pax
                      </span>
                    </div>
                    <span className="font-medium tabular-nums text-gray-900">
                      {formatCurrency(department.net)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <a href="/admin/reports/project-labour-cost" className="btn btn-admin-outline">
              <BarChart3 className="mr-2 h-4 w-4" />
              Project Labour Cost Report
            </a>
            <button
              type="button"
              onClick={onExportPayrollRegister}
              disabled={payrollRegisterExporting}
              className="btn btn-admin-outline"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {payrollRegisterExporting ? 'Exporting…' : 'Export Payroll Register'}
            </button>
          </div>
        </div>
      </AccordionSection>

      <div className="flex justify-start border-t border-gray-200 pt-4">
        <button type="button" onClick={onBack} className="btn btn-admin-outline">
          Back to Adjust &amp; Lock
        </button>
      </div>

      <PayslipPreviewModal
        isOpen={showPayslipPreviewModal}
        workers={workers.map((payroll) => ({
          id: payroll.id,
          name: payroll.employee_name || payroll.employee_staff_id,
          staff_id: payroll.employee_staff_id,
          net_pay: payroll.net_pay,
        }))}
        onClose={() => setShowPayslipPreviewModal(false)}
      />

      <Modal
        isOpen={showBankFileModal}
        onClose={closeBankFileModal}
        title={`${activeClassificationLabel} Payment File`}
        size="md"
        theme="admin"
        closable={!downloadBankFileMutation.isPending}
        footer={(
          <>
            <button
              type="button"
              className="btn btn-admin-outline"
              onClick={closeBankFileModal}
              disabled={downloadBankFileMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-admin-primary"
              onClick={handleBankFileDownload}
              disabled={
                !activePreview?.ready
                || !branchCodeReviewComplete
                || downloadBankFileMutation.isPending
              }
            >
              <Download className="mr-2 h-4 w-4" />
              {downloadBankFileMutation.isPending
                ? 'Generating…'
                : `Download ${(activePreview?.bank?.file_extension || 'File').toUpperCase()}`}
            </button>
          </>
        )}
      >
        {!activePreview ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Preparing preview…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{activePreview.bank?.name}</p>
                  <p className="mt-1 text-xs text-gray-500">{activePreview.bank?.file_format}</p>
                </div>
                {activePreview.ready && branchCodeReviewComplete ? (
                  <span className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Ready
                  </span>
                ) : activePreview.ready && branchCodeReviewRequired ? (
                  <span className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                    <Eye className="h-3.5 w-3.5" />
                    Review required
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Blocked
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="col-span-2 rounded-lg bg-gray-50 p-3 sm:col-span-1">
                <p className="text-xs text-gray-500">Payments</p>
                <p className="text-lg font-semibold text-gray-900">
                  {activePreview.payable_count} / {activePreview.expected_count}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">File Total</p>
                <p className="text-lg font-semibold tabular-nums text-gray-900">
                  {formatCurrency(activePreview.total_amount)}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Value Date</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(activePreview.value_date)}
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-600">
              This file contains {activeClassificationLabel.toLowerCase()} payments only.
              Upload it through {activePreview.bank?.name}&apos;s business-banking portal
              {activePreview.bank?.upload_instructions
                ? ` under ${activePreview.bank.upload_instructions}`
                : ''}.
              {activePreview.bank?.file_extension === 'csv'
                && ' Do not open or re-save it in Excel because account numbers can be reformatted.'}
            </p>

            <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs font-medium text-blue-800">
              Before authorising in the bank portal, confirm the imported payment count and
              total match the control figures above.
            </p>

            {!!activeBranchCodeSummary?.review_required_count && (
              <div className="overflow-hidden rounded-lg border border-amber-200">
                <div className="flex items-start gap-2 bg-amber-50 px-3 py-3">
                  <Eye className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-amber-900">
                      Review payee branch codes
                    </p>
                    <p className="mt-1 text-xs text-amber-800">
                      {activeBranchCodeSummary.derived} derived from bank account rules
                      {activeBranchCodeSummary.stored > 0
                        ? ` · ${activeBranchCodeSummary.stored} from verified employee details`
                        : ''}
                      {activeBranchCodeSummary.unresolved > 0
                        ? ` · ${activeBranchCodeSummary.unresolved} left blank for manual verification`
                        : ''}
                    </p>
                  </div>
                </div>

                <details className="border-t border-amber-200 bg-white">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500">
                    View {activeBranchCodeSummary.review_required_count} code
                    {activeBranchCodeSummary.review_required_count === 1 ? '' : 's'}
                  </summary>
                  <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto border-t border-gray-100">
                    {activeBranchCodeSummary.items.map((item) => (
                      <li key={item.staff_id} className="px-3 py-2 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">
                              {item.staff_id} — {item.name}
                            </p>
                            <p className="mt-0.5 text-gray-500">
                              {item.bank_name} · {item.account_masked}
                            </p>
                          </div>
                          <span className={`flex-shrink-0 rounded-md px-2 py-1 font-semibold ${
                            item.code
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                          >
                            {item.code || 'Blank'}
                          </span>
                        </div>
                        <p className="mt-1 text-amber-800">{item.rule}</p>
                      </li>
                    ))}
                  </ul>
                </details>

                <label className="flex cursor-pointer items-start gap-3 border-t border-amber-200 bg-amber-50 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={branchCodesReviewed}
                    onChange={(event) => setBranchCodesReviewed(event.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  />
                  <span className="text-xs font-medium text-amber-900">
                    I reviewed the derived codes and any blank values for this export.
                  </span>
                </label>
              </div>
            )}

            {activeBranchCodeSummary?.stored > 0
              && !activeBranchCodeSummary.review_required && (
                <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-xs text-green-800">
                    All {activeBranchCodeSummary.stored} payee branch codes come from
                    verified employee bank details.
                  </p>
                </div>
            )}

            {!activePreview.configured && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                <p className="text-xs text-red-800">
                  The company&apos;s {activePreview.bank?.name} debit account
                  is not configured. An HR Admin must complete the secure
                  bank-file setup
                  {canConfigureBankFiles ? (
                    <>
                      {' '}in{' '}
                      <a
                        href="/admin/settings?tab=bank-files"
                        className="font-medium underline hover:text-red-900"
                      >
                        Bank File Settings
                      </a>
                    </>
                  ) : (
                    ' before this file can be exported'
                  )}
                  .
                </p>
              </div>
            )}

            {!activePreview.all_records_locked && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <p className="text-xs text-amber-800">
                  {activePreview.unlocked_count} payroll record(s) remain unlocked in Step 3.
                </p>
              </div>
            )}

            {(activePreview.warnings?.length || 0) > 0 && (
              <div className="overflow-hidden rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 border-b border-orange-200 bg-orange-50 px-3 py-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-orange-600" />
                  <p className="text-xs font-medium text-orange-800">
                    {activePreview.warnings.length} beneficiary name warning(s)
                  </p>
                </div>
                <ul className="max-h-40 divide-y divide-gray-100 overflow-y-auto">
                  {activePreview.warnings.map((warning) => (
                    <li key={warning.staff_id} className="px-3 py-2 text-xs">
                      <p className="font-medium text-gray-800">
                        {warning.staff_id} — {warning.name}
                      </p>
                      <p className="mt-0.5 text-orange-700">{warning.warning}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(activePreview.blocking_issues?.length || 0) > 0 && (
              <div className="overflow-hidden rounded-lg border border-red-200">
                <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-3 py-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <p className="text-xs font-medium text-red-800">
                    {activePreview.blocking_issues.length} payable employee(s) require attention
                  </p>
                </div>
                <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto">
                  {activePreview.blocking_issues.map((issue) => (
                    <li
                      key={issue.staff_id}
                      className="flex items-start justify-between gap-3 px-3 py-2 text-xs"
                    >
                      <span className="flex-shrink-0 font-medium text-gray-800">
                        {issue.staff_id} — {issue.name}
                      </span>
                      <span className="text-right text-red-700">{issue.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(activePreview.not_payable?.length || 0) > 0 && (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-xs font-medium text-gray-700">
                    {activePreview.not_payable.length} employee(s) correctly omitted
                  </p>
                </div>
                <ul className="max-h-32 divide-y divide-gray-100 overflow-y-auto">
                  {activePreview.not_payable.map((item) => (
                    <li
                      key={item.staff_id}
                      className="flex items-start justify-between gap-3 px-3 py-2 text-xs"
                    >
                      <span className="flex-shrink-0 font-medium text-gray-800">
                        {item.staff_id} — {item.name}
                      </span>
                      <span className="text-right text-gray-500">{item.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={showReleasePayslipsModal}
        title="Release Worker Payslips"
        message={`Confirm that the bank payment files have been reviewed and authorised. This will make ${selectedPeriod || 'this period'}'s payslips visible to ${workers.length} worker(s) and send notifications. Staff are not affected. This cannot be undone.`}
        confirmLabel="Release Payslips"
        isLoading={releasePayslipsMutation.isPending}
        onConfirm={() => {
          releasePayslipsMutation.mutate({
            payrollRunId: currentRun.id,
            bank: effectiveBankId,
            bankFilesAuthorized: true,
          }, {
            onSuccess: () => setShowReleasePayslipsModal(false),
            onError: () => setShowReleasePayslipsModal(false),
          });
        }}
        onCancel={() => setShowReleasePayslipsModal(false)}
        theme="admin"
      />
    </div>
  );
};

export default PayrollPaymentStep;
